// 디렉토리 경로: client/src/utils/
// 파일명: referenceAnalysis.ts
// 파일 코드의 역할/설명: 레퍼런스 분석 결과 JSON을 검증하고 저장용 요약으로 변환한다.

import {
  REFERENCE_ANALYSIS_PANEL_SIZE,
  REFERENCE_ANALYSIS_SCORE_KEYS,
  type ReferenceAnalysisParseErrorCode,
  type ReferenceAnalysisResult,
  type ReferenceAnalysisScore,
  type ReferenceAnalysisScoreKey,
  type ReferenceAnalysisSummary,
  type ReferenceSourceType,
  // [v2] 가산 정규화용 서브 타입 (전부 옵셔널·비throw 대상)
  type ReferenceAnalysisTargetMode,
  type ReferenceEvidenceType,
  type ReferenceAnalysisResultStatus,
  type ReferenceDataQuality,
  type OrdinalBand,
  type ScaleLegend,
  type ConfigEcho,
  type HookAnalysis,
  type SuccessDimension,
  type PersonaScores,
  type SegmentBreakdownItem,
  type AudienceProfile,
  type PersonaCard,
  type PersonaProfile,
  type PersonaResponse,
  type FunnelPrediction,
  type FunnelStage,
  type ThumbStop,
  type TopLists,
  type ExpertPanelItem,
  type RedteamVote,
  type RiskAdjustedVerdict,
  type AudienceDrift,
  type GoalWeights,
} from '@/types'

export const REFERENCE_ANALYSIS_SCORE_LABELS: Record<
  ReferenceAnalysisScoreKey,
  string
> = {
  structure: '구조',
  topic: '주제',
  hook: '후킹',
  emotion: '감정',
  utility: '실용성',
  trust: '신뢰',
  cta: '행동 유도',
}

export class ReferenceAnalysisParseError extends Error {
  code: ReferenceAnalysisParseErrorCode

  constructor(code: ReferenceAnalysisParseErrorCode, message: string) {
    super(message)
    this.name = 'ReferenceAnalysisParseError'
    this.code = code
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

/**
 * 문자열/이스케이프를 인식하는 균형 중괄호 스캐너.
 * 첫 `{`부터 깊이를 추적해 첫 번째로 완결되는 객체만 잘라낸다.
 * (lastIndexOf('}')는 본문 뒤 트레일링 텍스트에 중괄호가 있으면 오염되므로 교체)
 */
function scanBalancedObject(text: string): string | null {
  const start = text.indexOf('{')
  if (start < 0) return null

  let depth = 0
  let inString = false
  let escaped = false

  for (let i = start; i < text.length; i++) {
    const ch = text[i]

    if (inString) {
      if (escaped) {
        escaped = false
      } else if (ch === '\\') {
        escaped = true
      } else if (ch === '"') {
        inString = false
      }
      continue
    }

    if (ch === '"') {
      inString = true
    } else if (ch === '{') {
      depth += 1
    } else if (ch === '}') {
      depth -= 1
      if (depth === 0) {
        return text.slice(start, i + 1)
      }
    }
  }

  return null
}

function extractJsonCandidate(rawText: string): string {
  const trimmed = rawText.trim()
  if (!trimmed) {
    throw new ReferenceAnalysisParseError(
      'PARSE_EMPTY',
      '분석 결과를 붙여넣어 주세요.'
    )
  }

  // (1)(2) 코드펜스 수집 → ```json 펜스 우선, 복수면 promptVersion 포함 펜스 선택.
  // 이중출력(Part A 마크다운 + Part B JSON) 붙여넣기에서도 Part B를 정확히 집는다.
  const fences: Array<{ lang: string; body: string }> = []
  const fenceRe = /```([a-zA-Z0-9]*)\s*([\s\S]*?)```/g
  let fenceMatch: RegExpExecArray | null
  while ((fenceMatch = fenceRe.exec(trimmed)) !== null) {
    const body = fenceMatch[2]?.trim()
    if (body) {
      fences.push({ lang: (fenceMatch[1] || '').toLowerCase(), body })
    }
  }

  if (fences.length > 0) {
    const jsonFences = fences.filter((fence) => fence.lang === 'json')
    const pool = jsonFences.length > 0 ? jsonFences : fences
    const withVersion = pool.find((fence) => fence.body.includes('promptVersion'))
    return (withVersion ?? pool[0]).body
  }

  // (3) 펜스 없음 → 균형 중괄호 스캐너로 첫 완결 객체 추출.
  const scanned = scanBalancedObject(trimmed)
  if (scanned) {
    return scanned
  }

  // 폴백: 레거시 휴리스틱(첫 { ~ 마지막 }).
  const start = trimmed.indexOf('{')
  const end = trimmed.lastIndexOf('}')
  if (start >= 0 && end > start) {
    return trimmed.slice(start, end + 1)
  }

  return trimmed
}

function assertNoUnsafeClaim(rawText: string): void {
  const unsafePatterns = [
    /실제\s*1,?000\s*명.{0,16}(투표|설문|응답|조사|참여)/i,
    /1,?000\s*명.{0,16}(실제\s*)?(투표\s*완료|설문\s*완료|응답\s*완료)/i,
    /실제\s*500\s*명.{0,16}(투표|설문|응답|조사|참여)/i,
    // 부정 룩어헤드: '실제 설문 결과가 아닙니다' 같은 정직성 고지를 자폭시키지 않는다.
    // ('아닙니다'는 '아닙' 음절로 시작하므로 '아니'만으로는 매칭 안 됨 → '아닙'도 포함)
    /실제\s*(투표|설문)\s*결과(?!\s*(?:가|이|은|는|를|을)?\s*(?:아니|아닌|아님|아닙))/i,
    /500\s*명.{0,16}(실제\s*)?(투표\s*완료|설문\s*완료|응답\s*완료)/i,
    /actual\s+survey\s+result/i,
    /real\s+vote\s+result/i,
  ]

  if (unsafePatterns.some((pattern) => pattern.test(rawText))) {
    throw new ReferenceAnalysisParseError(
      'PARSE_UNSAFE_CLAIM',
      '실제 투표나 실제 설문처럼 보이는 문구가 포함되어 있습니다.'
    )
  }
}

function parseJson(rawText: string): unknown {
  const candidate = extractJsonCandidate(rawText)

  try {
    return JSON.parse(candidate)
  } catch {
    throw new ReferenceAnalysisParseError(
      'PARSE_INVALID_JSON',
      'JSON 형식으로 파싱할 수 없습니다.'
    )
  }
}

function clampScore(value: unknown): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    throw new ReferenceAnalysisParseError(
      'PARSE_INVALID_SCHEMA',
      '점수는 숫자여야 합니다.'
    )
  }

  return Math.min(100, Math.max(0, Math.round(value)))
}

function requireString(value: unknown, fieldName: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new ReferenceAnalysisParseError(
      'PARSE_INVALID_SCHEMA',
      `${fieldName} 값이 필요합니다.`
    )
  }

  return value.trim()
}

function requireSourceType(value: unknown): ReferenceSourceType {
  if (value === 'saved' || value === 'tracking') {
    return value
  }

  throw new ReferenceAnalysisParseError(
    'PARSE_INVALID_SCHEMA',
    'sourceType은 saved 또는 tracking이어야 합니다.'
  )
}

function requireStringArray(value: unknown, fieldName: string): string[] {
  if (!Array.isArray(value)) {
    throw new ReferenceAnalysisParseError(
      'PARSE_INVALID_SCHEMA',
      `${fieldName} 배열이 필요합니다.`
    )
  }

  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)
}

function normalizeScores(value: unknown): Record<ReferenceAnalysisScoreKey, number> {
  if (!isRecord(value)) {
    throw new ReferenceAnalysisParseError(
      'PARSE_INVALID_SCHEMA',
      'scores 객체가 필요합니다.'
    )
  }

  return REFERENCE_ANALYSIS_SCORE_KEYS.reduce(
    (scores, key) => {
      scores[key] = clampScore(value[key])
      return scores
    },
    {} as Record<ReferenceAnalysisScoreKey, number>
  )
}

function normalizeWinningFactors(value: unknown): ReferenceAnalysisScore[] {
  if (!Array.isArray(value)) {
    throw new ReferenceAnalysisParseError(
      'PARSE_INVALID_SCHEMA',
      'winningFactors 배열이 필요합니다.'
    )
  }

  return value.map((item) => {
    if (!isRecord(item)) {
      throw new ReferenceAnalysisParseError(
        'PARSE_INVALID_SCHEMA',
        'winningFactors 항목 형식이 올바르지 않습니다.'
      )
    }

    const key = item.key
    if (
      typeof key !== 'string' ||
      !REFERENCE_ANALYSIS_SCORE_KEYS.includes(key as ReferenceAnalysisScoreKey)
    ) {
      throw new ReferenceAnalysisParseError(
        'PARSE_INVALID_SCHEMA',
        'winningFactors.key 값이 올바르지 않습니다.'
      )
    }

    const scoreKey = key as ReferenceAnalysisScoreKey
    return {
      key: scoreKey,
      label:
        typeof item.label === 'string' && item.label.trim()
          ? item.label.trim()
          : REFERENCE_ANALYSIS_SCORE_LABELS[scoreKey],
      score: clampScore(item.score),
      evidence: requireString(item.evidence, 'winningFactors.evidence'),
      reusablePattern: requireString(
        item.reusablePattern,
        'winningFactors.reusablePattern'
      ),
      // [v2] 가산 옵셔널. 불량/부재 → undefined(비throw).
      evidenceType: safeEvidenceType(item.evidenceType),
    }
  })
}

// ============================================================================
// [v2] 비throw 정규화 계층
// 모든 v2 필드는 옵셔널이며, 불량 입력은 throw가 아니라 undefined/[] 로 폴백한다.
// v1 코어 결여만 위(validate)에서 throw하고, v2 변형은 절대 throw하지 않는다.
// ============================================================================

function safeString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

function safeStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)
}

function clampToRange(
  value: unknown,
  min: number,
  max: number
): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  return Math.min(max, Math.max(min, value))
}

function safeNumberInRange(
  value: unknown,
  min: number,
  max: number
): number | undefined {
  return clampToRange(value, min, max)
}

/** 0~10 페르소나 스케일 전용(0~100 clamp에 절대 통과 금지). */
function clampScore10(value: unknown): number | undefined {
  return clampToRange(value, 0, 10)
}

function safeIntInRange(
  value: unknown,
  min: number,
  max: number
): number | undefined {
  const clamped = clampToRange(value, min, max)
  return clamped === undefined ? undefined : Math.round(clamped)
}

function safeEnum<T extends string>(
  value: unknown,
  allowed: readonly T[]
): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined
}

function safeEvidenceType(value: unknown): ReferenceEvidenceType | undefined {
  return safeEnum<ReferenceEvidenceType>(value, [
    'quote',
    'metric',
    'inference',
    'assumption',
  ])
}

function safeBand(value: unknown): OrdinalBand | undefined {
  return safeEnum<OrdinalBand>(value, ['상', '중', '하'])
}

/** 부재/불량 → 'reference'. 'reference'|'draft'만 통과. throw 금지. */
function normalizeTargetMode(value: unknown): ReferenceAnalysisTargetMode {
  return value === 'draft' ? 'draft' : 'reference'
}

type FieldNormalizer = (value: unknown) => unknown

/** 스펙 맵으로 객체를 얕게 정규화. 결과가 비면 undefined. record 아니면 undefined. */
function normalizeObject(
  value: unknown,
  spec: Record<string, FieldNormalizer>
): Record<string, unknown> | undefined {
  if (!isRecord(value)) return undefined
  const out: Record<string, unknown> = {}
  for (const [key, normalize] of Object.entries(spec)) {
    const normalized = normalize(value[key])
    if (normalized !== undefined) {
      out[key] = normalized
    }
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/** 배열의 record 항목만 매핑. 콜백이 undefined면 항목 드롭. */
function mapRecords<T>(
  value: unknown,
  map: (record: Record<string, unknown>) => T | undefined
): T[] | undefined {
  if (!Array.isArray(value)) return undefined
  const out: T[] = []
  for (const item of value) {
    if (isRecord(item)) {
      const mapped = map(item)
      if (mapped !== undefined) out.push(mapped)
    }
  }
  return out
}

const PERSONA_SCORES_SPEC: Record<string, FieldNormalizer> = {
  satisfaction: clampScore10,
  trust: clampScore10,
  demandFit: clampScore10,
}

function normalizePersonaScores(value: unknown): PersonaScores | undefined {
  return normalizeObject(value, PERSONA_SCORES_SPEC) as PersonaScores | undefined
}

function normalizeScaleLegend(value: unknown): ScaleLegend | undefined {
  return normalizeObject(value, {
    legacy: safeString,
    persona: safeString,
    intent: safeString,
  }) as ScaleLegend | undefined
}

function normalizeConfigEcho(value: unknown): ConfigEcho | undefined {
  return normalizeObject(value, {
    targetMode: safeString,
    platform: safeString,
    category: safeString,
    primaryGoal: safeString,
    metrics: (v) =>
      normalizeObject(v, {
        views: (n) => safeIntInRange(n, 0, Number.MAX_SAFE_INTEGER),
        likes: (n) => safeIntInRange(n, 0, Number.MAX_SAFE_INTEGER),
        comments: (n) => safeIntInRange(n, 0, Number.MAX_SAFE_INTEGER),
      }),
    personaCount: (n) => safeIntInRange(n, 0, 100000),
    outputMode: safeString,
    mustIncludeSegments: safeStringArray,
    language: safeString,
    scaleLegend: normalizeScaleLegend,
    reportPath: safeString,
    contentTruncated: (v) => (typeof v === 'boolean' ? v : undefined),
  }) as ConfigEcho | undefined
}

function normalizeHookAnalysis(value: unknown): HookAnalysis | undefined {
  return normalizeObject(value, {
    firstLine: safeString,
    foldPoint: safeString,
    hookType: safeString,
    scrollStopScore: clampScore10,
    whyStopsOrFails: safeString,
  }) as HookAnalysis | undefined
}

function normalizeGoalWeights(value: unknown): GoalWeights | undefined {
  if (!isRecord(value)) return undefined
  const out: GoalWeights = {}
  for (const key of REFERENCE_ANALYSIS_SCORE_KEYS) {
    const weight = safeNumberInRange(value[key], 0, 1)
    if (weight !== undefined) out[key] = weight
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function normalizeSuccessDimensions(
  value: unknown
): SuccessDimension[] | undefined {
  return mapRecords<SuccessDimension>(value, (record) => {
    const name = safeString(record.name)
    if (!name) return undefined
    return {
      name,
      band: safeBand(record.band),
      rationale: safeString(record.rationale),
      evidenceType: safeEvidenceType(record.evidenceType),
    }
  })
}

function normalizeAudienceProfile(value: unknown): AudienceProfile | undefined {
  return normalizeObject(value, {
    primary: safeString,
    secondary: safeString,
    antiAudience: safeString,
    oneLineReaderDef: safeString,
  }) as AudienceProfile | undefined
}

function normalizeSegmentBreakdown(
  value: unknown
): SegmentBreakdownItem[] | undefined {
  return mapRecords<SegmentBreakdownItem>(value, (record) => ({
    segmentId: safeString(record.segmentId),
    label: safeString(record.label),
    cellId: safeString(record.cellId),
    ratioPct: safeNumberInRange(record.ratioPct, 0, 100),
    avgScores: normalizePersonaScores(record.avgScores),
    intentPct: safeNumberInRange(record.intentPct, 0, 100),
  }))
}

function normalizePersonaProfile(value: unknown): PersonaProfile | undefined {
  return normalizeObject(value, {
    age: (n) => safeIntInRange(n, 0, 150),
    gender: safeString,
    region: safeString,
    job: safeString,
    digitalFluency: safeString,
    scrollBehavior: safeString,
    exposureContext: safeString,
    authorRelation: safeString,
    aiFatigue: safeString,
    saveShareHabit: safeString,
    coreGoal: safeString,
    fearResistance: safeString,
    decisionStyle: safeString,
    trustSensitivity: safeString,
    skepticism: safeString,
    selfIntro: safeString,
  }) as PersonaProfile | undefined
}

function normalizePersonaResponse(value: unknown): PersonaResponse | undefined {
  return normalizeObject(value, {
    firstImpression: safeString,
    trust: clampScore10,
    comprehension: clampScore10,
    relevance: clampScore10,
    gotVsWanted: safeString,
    liked: safeString,
    disliked: safeString,
    frictionToAct: safeString,
    behaviorPrediction: safeStringArray,
    satisfaction: clampScore10,
    trustScore: clampScore10,
    demandFit: clampScore10,
    intentPct: (n) => safeNumberInRange(n, 0, 100),
    reason: safeString,
  }) as PersonaResponse | undefined
}

function normalizePersonaCards(value: unknown): PersonaCard[] | undefined {
  return mapRecords<PersonaCard>(value, (record) => {
    const id = safeString(record.id)
    if (!id) return undefined // 안정 key 보장(id 필수)
    return {
      id,
      cellId: safeString(record.cellId),
      name: safeString(record.name),
      segment: safeString(record.segment),
      profile: normalizePersonaProfile(record.profile),
      response: normalizePersonaResponse(record.response),
    }
  })
}

function normalizeFunnelPrediction(
  value: unknown
): FunnelPrediction | undefined {
  return normalizeObject(value, {
    thumbStop3s: (v) =>
      normalizeObject(v, {
        band: safeBand,
        passProb: (n) => safeNumberInRange(n, 0, 1),
      }) as ThumbStop | undefined,
    stages: (v) =>
      mapRecords<FunnelStage>(v, (record) => ({
        stage: safeString(record.stage),
        band: safeBand(record.band),
        dropPoint: safeString(record.dropPoint),
        population: safeString(record.population),
      })),
    note: safeString,
  }) as FunnelPrediction | undefined
}

function normalizeTopLists(value: unknown): TopLists | undefined {
  return normalizeObject(value, {
    wantedInfo: safeStringArray,
    complaints: safeStringArray,
    reactionKillers: safeStringArray,
    highResponseSegments: safeStringArray,
    lowResponseSegments: safeStringArray,
  }) as TopLists | undefined
}

function normalizeExpertPanel(value: unknown): ExpertPanelItem[] | undefined {
  return mapRecords<ExpertPanelItem>(value, (record) => {
    const role = safeString(record.role)
    if (!role) return undefined
    return {
      role,
      finding: safeString(record.finding),
      leverMapping: safeString(record.leverMapping),
      evidenceType: safeEvidenceType(record.evidenceType),
    }
  })
}

function normalizeRedteamVotes(value: unknown): RedteamVote[] | undefined {
  return mapRecords<RedteamVote>(value, (record) => {
    const role = safeString(record.role)
    if (!role) return undefined
    return {
      role,
      claim: safeString(record.claim),
      verdict: safeString(record.verdict),
      counterexample: safeString(record.counterexample),
      verificationMethod: safeString(record.verificationMethod),
      voteBasis: safeString(record.voteBasis),
      confidence: safeNumberInRange(record.confidence, 0, 1),
    }
  })
}

function normalizeRiskAdjustedVerdict(
  value: unknown
): RiskAdjustedVerdict | undefined {
  return normalizeObject(value, {
    summary: safeString,
    weightedConfidence: (n) => safeNumberInRange(n, 0, 1),
    topRisks: safeStringArray,
  }) as RiskAdjustedVerdict | undefined
}

function normalizeAudienceDrift(value: unknown): AudienceDrift | undefined {
  return normalizeObject(value, {
    currentAudience: safeString,
    intendedAudience: safeString,
    gap: safeString,
    priorityHint: safeString,
  }) as AudienceDrift | undefined
}

// v1 코어 키 + 타입드 v2 키. 그 외 키는 extras로 verbatim 보존.
const V1_CORE_KEYS = new Set<string>([
  'id',
  'sourceReferenceId',
  'sourceType',
  'promptVersion',
  'simulatedPanelSize',
  'scores',
  'winningFactors',
  'extractedTopics',
  'extractedStructures',
  'extractedIdeas',
  'cautions',
  'createdAt',
])

const TYPED_V2_KEYS = new Set<string>([
  'schemaVersion',
  'targetMode',
  'status',
  'statusReason',
  'dataQuality',
  'sensitiveDomain',
  'panelLabel',
  'personaCount',
  'narratedPersonaCount',
  'archetypeCount',
  'extrapolationNote',
  'scaleLegend',
  'configEcho',
  'goalAlignedScore',
  'goalWeights',
  'hookAnalysis',
  'successDimensions',
  'audienceProfile',
  'segmentBreakdown',
  'personaScores',
  'personaCards',
  'funnelPrediction',
  'topLists',
  'expertPanel',
  'redteamVotes',
  'riskAdjustedVerdict',
  'attributionCaveats',
  'improvements',
  'audienceDrift',
  'goalLeverGap',
  'methodologyNote',
  'limitations',
  'extras',
])

function collectExtras(
  input: Record<string, unknown>
): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {}

  // 1) 미지 키 자동 수집(코어/타입드 키 제외) — 얕은 verbatim 보존.
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) continue
    if (V1_CORE_KEYS.has(key) || TYPED_V2_KEYS.has(key)) continue
    out[key] = value
  }

  // 2) AI가 명시적으로 출력한 input.extras(plain object)를 병합.
  //    충돌 시 명시 input.extras 값을 우선(나중에 덮어씀).
  //    단, v1 코어/타입드 키는 어떤 경로로도 extras로 새지 않게 제외한다.
  if (isRecord(input.extras)) {
    for (const [key, value] of Object.entries(input.extras)) {
      if (value === undefined) continue
      if (V1_CORE_KEYS.has(key) || TYPED_V2_KEYS.has(key)) continue
      out[key] = value
    }
  }

  return Object.keys(out).length > 0 ? out : undefined
}

/**
 * v1 코어 위에 v2 옵셔널 필드를 비throw로 정규화해 append한다.
 * 코어 키는 절대 건드리지 않으며, 어떤 변형도 throw하지 않는다.
 */
function normalizeV2Fields(
  input: Record<string, unknown>
): Partial<ReferenceAnalysisResult> {
  const out: Partial<ReferenceAnalysisResult> = {}

  out.schemaVersion = safeEnum(input.schemaVersion, [
    'reference-analysis-v2',
  ] as const)
  out.targetMode = normalizeTargetMode(input.targetMode) // 기본 reference
  out.status = safeEnum<ReferenceAnalysisResultStatus>(input.status, [
    'ok',
    'degraded',
    'insufficient_input',
  ])
  out.statusReason = safeString(input.statusReason)
  out.dataQuality = safeEnum<ReferenceDataQuality>(input.dataQuality, [
    'high',
    'medium',
    'low',
  ])
  out.sensitiveDomain = safeString(input.sensitiveDomain)

  out.panelLabel = safeString(input.panelLabel)
  out.personaCount = safeIntInRange(input.personaCount, 0, 100000)
  out.narratedPersonaCount = safeIntInRange(input.narratedPersonaCount, 0, 100000)
  out.archetypeCount = safeIntInRange(input.archetypeCount, 0, 100000)
  out.extrapolationNote = safeString(input.extrapolationNote)
  out.scaleLegend = normalizeScaleLegend(input.scaleLegend)
  out.configEcho = normalizeConfigEcho(input.configEcho)

  out.goalAlignedScore = safeIntInRange(input.goalAlignedScore, 0, 100)
  out.goalWeights = normalizeGoalWeights(input.goalWeights)
  out.hookAnalysis = normalizeHookAnalysis(input.hookAnalysis)
  out.successDimensions = normalizeSuccessDimensions(input.successDimensions)

  out.audienceProfile = normalizeAudienceProfile(input.audienceProfile)
  out.segmentBreakdown = normalizeSegmentBreakdown(input.segmentBreakdown)
  out.personaScores = normalizePersonaScores(input.personaScores)
  out.personaCards = normalizePersonaCards(input.personaCards)
  out.funnelPrediction = normalizeFunnelPrediction(input.funnelPrediction)
  out.topLists = normalizeTopLists(input.topLists)

  out.expertPanel = normalizeExpertPanel(input.expertPanel)
  out.redteamVotes = normalizeRedteamVotes(input.redteamVotes)
  out.riskAdjustedVerdict = normalizeRiskAdjustedVerdict(input.riskAdjustedVerdict)

  out.attributionCaveats = safeStringArray(input.attributionCaveats)
  out.improvements = safeStringArray(input.improvements)
  out.audienceDrift = normalizeAudienceDrift(input.audienceDrift)
  out.goalLeverGap = safeStringArray(input.goalLeverGap)

  out.methodologyNote = safeString(input.methodologyNote)
  out.limitations = safeString(input.limitations)

  out.extras = collectExtras(input)

  return out
}

export function validateReferenceAnalysisResult(
  input: unknown
): ReferenceAnalysisResult {
  if (!isRecord(input)) {
    throw new ReferenceAnalysisParseError(
      'PARSE_INVALID_SCHEMA',
      '분석 결과 객체가 필요합니다.'
    )
  }

  if (input.promptVersion !== 'reference-analysis-v1') {
    throw new ReferenceAnalysisParseError(
      'PARSE_INVALID_SCHEMA',
      'promptVersion이 reference-analysis-v1이어야 합니다.'
    )
  }

  if (input.simulatedPanelSize !== REFERENCE_ANALYSIS_PANEL_SIZE) {
    throw new ReferenceAnalysisParseError(
      'PARSE_INVALID_SCHEMA',
      `simulatedPanelSize는 ${REFERENCE_ANALYSIS_PANEL_SIZE}이어야 합니다.`
    )
  }

  // v1 코어 (게이트 통과분만 build; 결여 시 위/아래 require*가 throw).
  const core: ReferenceAnalysisResult = {
    id: requireString(input.id, 'id'),
    sourceReferenceId: requireString(
      input.sourceReferenceId,
      'sourceReferenceId'
    ),
    sourceType: requireSourceType(input.sourceType),
    promptVersion: 'reference-analysis-v1',
    simulatedPanelSize: REFERENCE_ANALYSIS_PANEL_SIZE,
    scores: normalizeScores(input.scores),
    winningFactors: normalizeWinningFactors(input.winningFactors),
    extractedTopics: requireStringArray(
      input.extractedTopics,
      'extractedTopics'
    ),
    extractedStructures: requireStringArray(
      input.extractedStructures,
      'extractedStructures'
    ),
    extractedIdeas: requireStringArray(input.extractedIdeas, 'extractedIdeas'),
    cautions: requireStringArray(input.cautions, 'cautions'),
    createdAt: requireString(input.createdAt, 'createdAt'),
  }

  // v2 옵셔널 필드를 비throw 정규화로 가산(코어 키는 미변경).
  return { ...core, ...normalizeV2Fields(input) }
}

export function parseReferenceAnalysisInput(
  rawText: string
): ReferenceAnalysisResult {
  assertNoUnsafeClaim(rawText)
  return validateReferenceAnalysisResult(parseJson(rawText))
}

export function makeReferenceAnalysisSummary(
  result: ReferenceAnalysisResult
): ReferenceAnalysisSummary {
  const [topScoreKey, topScore] = Object.entries(result.scores).sort(
    ([, a], [, b]) => b - a
  )[0] as [ReferenceAnalysisScoreKey, number]

  return {
    promptVersion: result.promptVersion,
    simulatedPanelSize: result.simulatedPanelSize,
    topScoreKey,
    topScore,
    topFactors: result.winningFactors
      .slice()
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((factor) => factor.reusablePattern),
    extractedTopics: result.extractedTopics.slice(0, 5),
    createdAt: result.createdAt,
  }
}
